import React, { useState } from 'react'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { clearSsoIdentity } from '../../../../../../shared/sso-workspace/auth-api'
import { serverMessage } from '../../../../../../shared/error/server-message'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { useAuthContext } from '../../../../../../context/auth.context'
import { useNotificationContext } from '../../../../../../context/notification/context'
import { useRoleOrgUser } from '../../../../../../role/hook/role-org'
import { connectionLabel, isManaged } from '../common/managed-by'

// "Reset sign-in link": clears the identity provider's account id that auth
// recorded at this person's first sign-in through the connection that manages
// them (DELETE sso-identity/:loginID/:providerID), for when they were
// recreated in the identity provider and are now refused as
// identity_mismatch. Their next sign-in links the new account. Shown with
// ORG USER delete, and not to an impersonated session (auth refuses it).
//
// Who it helps (owner question 2026-10-29: "can it help a person who must
// use SSO?"): exactly them. Auth records the Microsoft account (directory
// and object id) at a person's first sign-in through a Microsoft connection
// and refuses a later sign-in from a different account with the same
// address (the immutable-id check, G3) -- someone deleted and re-created
// in Microsoft Entra ID. The reset clears that record; nothing else about
// how they sign in changes (Require SSO stays). Other connections only
// record the account and never refuse, so the action is offered only where
// content says the connection is Microsoft's (managedBy.vendor); a content
// that doesn't say yet (no vendor field) keeps offering it.
export const MICROSOFT = 'Microsoft Entra ID'
export const resetHelps = managedBy => !!managedBy && (!('vendor' in managedBy) || managedBy.vendor === MICROSOFT)

export const RESET_EXPLAIN = 'If they were deleted and re-created in Microsoft Entra ID, their sign-in is refused as a different Microsoft account. Resetting lets their next single sign-on link the new account. How they sign in doesn’t change.'

export const RESET_SUCCESS = 'Reset. Their next sign-in links their account again.'
export const RESET_NOTHING = "Nothing to reset: they haven't signed in through this connection yet."
export const RESET_FAILED = "The sign-in link couldn't be reset."

export const resetBody = managedBy =>
  `Use this when this person was recreated in your identity provider and can't sign in. Their next sign-in through ${connectionLabel(managedBy) || 'your identity provider'} links their new account.`

export const ResetSignInLink = ({ userLogin }) => {
  const role = useRoleOrgUser()
  const { state } = useAuthContext()
  const notification = useNotificationContext()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const managedBy = userLogin && userLogin.managedBy
  const impersonating = !!(state && state.jwtClaims && state.jwtClaims.impersonationEndsAt)
  if (!isManaged(managedBy) || !resetHelps(managedBy) || !managedBy.connectionID || !userLogin.loginID || !role.accessDelete || impersonating) return null

  const close = () => { setOpen(false); setError(null) }

  const confirm = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await clearSsoIdentity(userLogin.loginID, managedBy.connectionID)
      setBusy(false)
      setOpen(false)
      if (result && result.cleared) notification.toast.notify(RESET_SUCCESS)
      else notification.toast.notify(RESET_NOTHING)
    } catch (err) {
      setBusy(false)
      setError(serverMessage(err, RESET_FAILED), err)
    }
  }

  return (
    <React.Fragment>
      <div className="pp-reset mt-3" data-testid="reset-sign-in">
        <div className="sw-hint">{RESET_EXPLAIN}</div>
        <button type="button" className="btn btn-sm sw-btn mt-2" onClick={() => setOpen(true)}>Reset sign-in link</button>
      </div>
      <ConfirmDialog
        open={open}
        title="Reset sign-in link?"
        body={resetBody(managedBy)}
        confirmText="Reset sign-in link"
        confirmClassName="btn sw-btn-primary"
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={confirm}
        onCancel={close}
      />
    </React.Fragment>
  )
}
